import React from 'react';
import { Affix, Button, Tag } from 'antd';
import { Product } from 'models/SupplierDynamics';
import { useRecoilValue } from 'recoil';
import { contentRefState } from 'recoil/atoms';
import './Compare-footer.scss';
import { CloseCircleFilled, DatabaseFilled } from '@ant-design/icons';

interface CompareFooterProps {
  /**
   * Array of products to display in the footer
   */
  products: Product[];
  /**
   * Callback function called when the Compare button is clicked
   */
  onCompare?: () => void;
  /**
   * Optional callback function to remove a product from the selection
   */
  onRemoveProduct?: (product: Product) => void;
}

/**
 * CompareFooter component displays selected products in an affixed footer with a Compare button.
 * This component uses Ant Design's Affix to stick to the bottom of the viewport.
 * @param {CompareFooterProps} props - Component props
 * @param {Product[]} props.products - Array of selected products to display
 * @param {() => void} [props.onCompare] - Callback function when Compare button is clicked
 * @param {(product: Product) => void} [props.onRemoveProduct] - Optional callback to remove a product
 * @returns {React.ReactElement} The rendered CompareFooter component
 */
export const CompareFooter: React.FC<CompareFooterProps> = ({
  products,
  onCompare,
  onRemoveProduct
}): React.ReactElement => {
  /**
   * Handles the Compare button click
   */
  const contentRef = useRecoilValue(contentRefState);
  const handleCompare = () => {
    if (onCompare) {
      onCompare();
    }
  };

  /**
   * Handles removing a product from selection
   * @param {Product} product - The product to remove
   */
  const handleRemoveProduct = (product: Product) => {
    if (onRemoveProduct) {
      onRemoveProduct(product);
    }
  };

  // Don't render if no products are selected
  if (products.length === 0) {
    return <></>;
  }

  return (
    <Affix target={() => contentRef!.current!} className="rounded-xl border-t border-gray-200" offsetBottom={0}>
      <h4 className="text-md font-bold text-center bg-white mb-0 pt-2  shadow-lg">SELECTED PRODUCTS</h4>
      <div className="bg-white  shadow-lg px-6 py-4 flex items-center justify-center gap-4">
        {products.map((product, index) => (
          <div key={index} className="slot-tag">
            <h1 className="product-name line-clamp-2">{product.name}</h1>
            <Button
              type="text"
              icon={<CloseCircleFilled />}
              onClick={() => handleRemoveProduct(product)}
              className="close-button"
            />
          </div>
        ))}

        {/* Compare Button */}
        <Button
          color="link"
          size="large"
          onClick={handleCompare}
          className="compare-button"
          disabled={products.length === 0}
        >
          <DatabaseFilled className="text-3xl" />
          <p>Find Competitors</p>
        </Button>
      </div>
    </Affix>
  );
};
